import { useCallback, useRef, type RefObject } from 'react';
import type { ProjectPreviewRef } from '@/components/ProjectPreview';
import { useCoverFrameCapture } from '@/hooks/use-cover-frame-capture';
import { clipTimeToVideoTime } from '@/lib/layers/clipTimeToVideoTime';
import type { CoverCropFraming } from '@/lib/cover/resolveCropFramingAtTime';
import { downscaleDataUrl } from '@/lib/media/downscaleDataUrl';
import { EXPORT_FPS } from '@/lib/timeline/exportFrameTiming';
import type { SceneTransition } from '@/lib/transitions/types';
import {
  CAPTURE_JPEG_QUALITY,
  CAPTURE_MAX_SIDE,
  normalizeCaptureTimes,
  resolvePlayerFrame,
} from '@/lib/ai-assistant/editor/frameCapture';
import {
  captureFrameAt,
  PREVIEW_RENDER_BUDGET_MS,
  SOURCE_FRAME_BUDGET_MS,
  type CapturedFrame,
} from '@/lib/ai-assistant/editor/captureFrame';

// Frames of the open clip for the AI assistant (editor_capture_frames and the
// composer's camera button). Each frame is rendered as the preview draws it
// (layout, subtitles, layers, effects) through the cover "Prévia" renderer,
// at a given frame, without moving the playhead. If that fails, the raw source
// frame (cropped like the cover capture) is used and marked source "video".
// Frames are shrunk to 720 px and uploaded; never registered in My Media.

export type { CapturedFrame } from '@/lib/ai-assistant/editor/captureFrame';

// editor_set_cover keeps the still at full size (the worker cover-fits it).
const COVER_MAX_SIDE = 1920;
const COVER_JPEG_QUALITY = 0.92;
const FRAME_SIZE = { maxSide: CAPTURE_MAX_SIDE, quality: CAPTURE_JPEG_QUALITY };

export interface EditorFrameCaptureInput {
  previewRef: RefObject<ProjectPreviewRef>;
  /** Full-quality source: the still renderer and the fallback read it. */
  videoUrl: string | undefined;
  exportAspect: number;
  resolveCropFraming?: (sourceTime: number) => CoverCropFraming | null;
  intervals: Array<{ startTime: number; endTime: number }>;
  transitions: SceneTransition[];
  /** Effective Player axis: selected scenes while playing or navigation-locked. */
  locked: boolean;
  fullDuration: number;
  clipDuration: number;
  /** The playhead on the clip timeline. */
  playhead: number;
}

export function useEditorFrameCapture(input: EditorFrameCaptureInput) {
  const inputRef = useRef(input);
  inputRef.current = input;
  const { captureFramedDataUrl, uploadFramedDataUrl } = useCoverFrameCapture({
    videoUrl: input.videoUrl,
    exportAspect: input.exportAspect,
    resolveCropFraming: input.resolveCropFraming,
  });

  const captureOne = useCallback(async (time: number, size = FRAME_SIZE): Promise<CapturedFrame> => {
    const current = inputRef.current;
    const sourceTime = clipTimeToVideoTime(time, current.intervals);
    const frame = resolvePlayerFrame({
      sourceTime,
      locked: current.locked,
      intervals: current.intervals,
      transitions: current.transitions,
      fps: EXPORT_FPS,
      fullDuration: current.fullDuration,
    });
    return captureFrameAt({ time, sourceTime, frame }, {
      renderPreview: async (playerFrame, signal) => (
        await inputRef.current.previewRef.current?.renderCurrentFrameStill({
          videoUrl: inputRef.current.videoUrl,
          frame: playerFrame,
          signal,
        })
      ) ?? null,
      extractSource: async (at) => (await captureFramedDataUrl(at))?.dataUrl ?? null,
      shrink: (dataUrl) => downscaleDataUrl(dataUrl, size.maxSide, size.quality),
      upload: async (dataUrl, at) => {
        const uploaded = await uploadFramedDataUrl(dataUrl, at);
        return uploaded.ok ? uploaded.url : null;
      },
      // The web renderer cannot finish in a hidden tab (rAF is paused).
      isPageHidden: () => document.visibilityState === 'hidden',
      previewBudgetMs: PREVIEW_RENDER_BUDGET_MS,
      sourceBudgetMs: SOURCE_FRAME_BUDGET_MS,
    });
  }, [captureFramedDataUrl, uploadFramedDataUrl]);

  /** `times` as the model sent them (clip seconds); normalized here. */
  const captureFrames = useCallback(async (times: unknown): Promise<CapturedFrame[]> => {
    const current = inputRef.current;
    const normalized = normalizeCaptureTimes(times, {
      playhead: current.playhead,
      duration: current.clipDuration,
    });
    const frames: CapturedFrame[] = [];
    // One at a time: the still renderer runs one render at a time anyway.
    for (const time of normalized) {
      frames.push(await captureOne(time));
    }
    return frames;
  }, [captureOne]);

  const captureCurrentFrame = useCallback(async (): Promise<CapturedFrame | null> => {
    const [frame] = await captureFrames(undefined);
    return frame ?? null;
  }, [captureFrames]);

  /** editor_set_cover: the frame at a clip time, full size, uploaded. */
  const captureCover = useCallback(async (time: number): Promise<{ url: string; sourceTime: number } | null> => {
    const current = inputRef.current;
    const at = Math.min(Math.max(0, time), current.clipDuration);
    const frame = await captureOne(at, { maxSide: COVER_MAX_SIDE, quality: COVER_JPEG_QUALITY });
    return 'url' in frame ? { url: frame.url, sourceTime: clipTimeToVideoTime(at, current.intervals) } : null;
  }, [captureOne]);

  return { captureFrames, captureCurrentFrame, captureCover };
}
